import React, { useMemo, useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";

const FIELD_ALIASES = {
  occupationstatus: ["occupationstatus", "occupation_status"],
  location: ["location", "address"],
  exp_garv: ["exp_garv", "expected_garv", "expgarv"],
  admin_garv: ["admin_garv", "admingarv"],
  pin: ["pin", "pin_id", "property_pin", "property_id", "id", "pin_number", "pin_no", "pinno"],
  circle: ["circle"],
  floor_admin: ["floor_admin", "floor", "floors", "no_of_floors", "no_of_floor", "total_floors", "stories", "storeys"],
  landuse_admin: ["landuse_admin", "land_use_admin", "admin_land_use", "admin_landuse", "landuse", "land_use", "landuse_type", "land_use_type", "land_type", "usage"],
  coveredarea_admin: ["coveredarea_admin", "covered_area_admin", "coverederarea_admin", "covereder_area_admin", "coveredarea", "covered_area", "coveredarea_sq_ft", "covered_area_sq_ft", "coveredarea_sqft", "covered_area_sqft"],
  landarea_admin: ["landarea_admin", "land_area_admin", "admin_landarea", "admin_land_area", "landarea", "land_area", "landarea_sq_yds", "land_area_sq_yds", "landarea_sqyds", "land_area_sqyds"],
  floors_pred: ["floors_pred", "floors_ml"],
  land_use_pred: ["land_use_pred", "landuse_ml"],
  coveredarea_combined_pred: ["coveredarea_combined_pred", "coveredarea_ml"],
  landarea_combined_pred: ["landarea_combined_pred", "landarea_ml"],
  floors_ml: ["floors_ml", "floors_pred"],
  landuse_ml: ["landuse_ml", "land_use_pred"],
  coveredarea_ml: ["coveredarea_ml", "coverederarea_ml", "covereder_area_ml", "coveredarea_combined_pred"],
  landarea_ml: ["landarea_ml", "landarea_combined_pred"],
  refno: ["refno", "reference_no", "reference_number", "pt5_reference", "pt_5_reference"],
  reassess: ["reassess"],
  group: ["group", "groupes"],
  p_name: ["p_name", "inspector_name"],
};

const normalizeKey = (key) =>
  String(key || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

const getValueFromRow = (row, aliases) => {
  const normalized = Object.entries(row).reduce((acc, [k, v]) => {
    acc[normalizeKey(k)] = v;
    return acc;
  }, Object.create(null));

  const match = aliases.find((alias) => normalized[alias] !== undefined && normalized[alias] !== null);
  return match ? normalized[match] : "";
};

const getPinFromFileName = (name) =>
  String(name || "")
    .replace(/\.[^/.]+$/, "")
    .trim();

const readImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const dims = { width: img.width, height: img.height };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Unable to read image ${file.name}`));
    };
    img.src = url;
  });

// NOTE: Frontend rotateToPortrait has been DISABLED (commented out).
// Rationale: Browser EXIF handling is inconsistent across vendors — some browsers return
// raw pixel dimensions for img.width/height while drawing EXIF-corrected pixels via
// ctx.drawImage(). This mismatch caused double-rotation of mobile photos taken in
// portrait orientation, resulting in wrongly oriented saved images. The backend Sharp
// pipeline (sharp().rotate() -> panorama check -> force-portrait) handles EXIF reliably
// across all clients; re-enable this helper only if you need client-side pre-processing.
//
// const PANORAMA_ASPECT_RATIO_THRESHOLD = 2.5;
//
// const rotateToPortrait = (file) =>
//   new Promise((resolve, reject) => {
//     const img = new Image();
//     const url = URL.createObjectURL(file);
//
//     img.onload = () => {
//       try {
//         const { width, height } = img;
//
//         if (height >= width) {
//           URL.revokeObjectURL(url);
//           resolve(file);
//           return;
//         }
//
//         const aspectRatio = width / height;
//         if (aspectRatio > PANORAMA_ASPECT_RATIO_THRESHOLD) {
//           URL.revokeObjectURL(url);
//           resolve(file);
//           return;
//         }
//
//         const canvas = document.createElement("canvas");
//         canvas.width = height;
//         canvas.height = width;
//         const ctx = canvas.getContext("2d");
//         if (!ctx) {
//           URL.revokeObjectURL(url);
//           resolve(file);
//           return;
//         }
//
//         ctx.translate(canvas.width / 2, canvas.height / 2);
//         ctx.rotate(Math.PI / 2);
//         ctx.drawImage(img, -width / 2, -height / 2);
//
//         canvas.toBlob(
//           (blob) => {
//             URL.revokeObjectURL(url);
//             if (!blob) {
//               resolve(file);
//               return;
//             }
//             resolve(new File([blob], file.name, { type: "image/jpeg" }));
//           },
//           "image/jpeg",
//           0.92
//         );
//       } catch (error) {
//         URL.revokeObjectURL(url);
//         reject(error);
//       }
//     };
//
//     img.onerror = () => {
//       URL.revokeObjectURL(url);
//       reject(new Error(`Unable to process image ${file.name}`));
//     };
//
//     img.src = url;
//   });

const BulkUpload = () => {
  const user = JSON.parse(localStorage.getItem("user")) || { id: "", name: "Unknown" };
  const [excelData, setExcelData] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");
  const [imagesLabel, setImagesLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const excelPins = useMemo(
    () => new Set(excelData.map((row) => String(row.pin || "").trim()).filter(Boolean)),
    [excelData]
  );

  const matchedImages = useMemo(() => {
    const matches = {};
    selectedImages.forEach((file) => {
      const pin = getPinFromFileName(file.name);
      if (excelPins.has(pin)) {
        matches[pin] = file;
      }
    });
    return matches;
  }, [selectedImages, excelPins]);

  const unmatchedImages = useMemo(
    () =>
      selectedImages.filter((file) => {
        const pin = getPinFromFileName(file.name);
        return !excelPins.has(pin);
      }),
    [selectedImages, excelPins]
  );

  const excelRowByPin = useMemo(() => {
    const map = {};
    excelData.forEach((row) => {
      if (row.pin) map[row.pin] = row;
    });
    return map;
  }, [excelData]);

  const readSheetFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.onload = (e) => resolve(e.target?.result);
      const name = String(file?.name || "").toLowerCase();
      if (name.endsWith(".csv")) reader.readAsText(file);
      else reader.readAsArrayBuffer(file);
    });

  const parseRowsFromFile = (file, fileContent) => {
    const name = String(file?.name || "").toLowerCase();
    if (name.endsWith(".csv")) {
      const parsed = Papa.parse(String(fileContent || ""), {
        header: true,
        skipEmptyLines: true,
      });
      if (parsed.errors?.length) {
        throw new Error(parsed.errors[0]?.message || "CSV parse error");
      }
      return Array.isArray(parsed.data) ? parsed.data : [];
    }

    const buffer = new Uint8Array(fileContent);
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheetName = workbook.SheetNames?.[0];
    if (!sheetName) return [];
    const worksheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false });
  };

  const handleExcelUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setCsvFileName("");
      return;
    }
    setCsvFileName(file.name);

    readSheetFile(file)
      .then((fileContent) => {
        const rows = parseRowsFromFile(file, fileContent);
        const formatted = rows
          .map((row) => {
            const mapped = {
              occupationstatus: getValueFromRow(row, FIELD_ALIASES.occupationstatus),
              location: getValueFromRow(row, FIELD_ALIASES.location),
              exp_garv: getValueFromRow(row, FIELD_ALIASES.exp_garv),
              admin_garv: getValueFromRow(row, FIELD_ALIASES.admin_garv),
              pin: String(getValueFromRow(row, FIELD_ALIASES.pin) || "").trim(),
              circle: getValueFromRow(row, FIELD_ALIASES.circle),
              floor_admin: getValueFromRow(row, FIELD_ALIASES.floor_admin),
              landuse_admin: getValueFromRow(row, FIELD_ALIASES.landuse_admin),
              coveredarea_admin: getValueFromRow(row, FIELD_ALIASES.coveredarea_admin),
              landarea_admin: getValueFromRow(row, FIELD_ALIASES.landarea_admin),
              floors_pred: getValueFromRow(row, FIELD_ALIASES.floors_pred),
              land_use_pred: getValueFromRow(row, FIELD_ALIASES.land_use_pred),
              coveredarea_combined_pred: getValueFromRow(row, FIELD_ALIASES.coveredarea_combined_pred),
              landarea_combined_pred: getValueFromRow(row, FIELD_ALIASES.landarea_combined_pred),
              floors_ml: getValueFromRow(row, FIELD_ALIASES.floors_ml),
              landuse_ml: getValueFromRow(row, FIELD_ALIASES.landuse_ml),
              coveredarea_ml: getValueFromRow(row, FIELD_ALIASES.coveredarea_ml),
              landarea_ml: getValueFromRow(row, FIELD_ALIASES.landarea_ml),
              refno: getValueFromRow(row, FIELD_ALIASES.refno),
              reassess: getValueFromRow(row, FIELD_ALIASES.reassess),
              group: getValueFromRow(row, FIELD_ALIASES.group),
              p_name: getValueFromRow(row, FIELD_ALIASES.p_name),
              status: "new",
              added_by: user.id,
            };
            return mapped;
          })
          .filter((row) => row.pin);

        // Ensure unique PINs from sheet
        const uniqueByPin = [];
        const seenPins = new Set();
        formatted.forEach(row => {
          if (!seenPins.has(row.pin)) {
            seenPins.add(row.pin);
            uniqueByPin.push(row);
          }
        });

        setExcelData(uniqueByPin);
        toast.success(`${uniqueByPin.length} unique rows loaded`);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Invalid file format");
        setExcelData([]);
      });
  };

  const handleImagesUpload = (event) => {
    const files = Array.from(event.target.files || []);
    const jpgFiles = files.filter((file) =>
      /image\/jpeg|image\/jpg|image\/png/i.test(file.type) || /\.(jpe?g|png)$/i.test(file.name)
    );

    // Ensure unique images by PIN to prevent "double pictures"
    const uniqueImagesByPin = [];
    const seenImagePins = new Set();

    jpgFiles.forEach(file => {
      const pin = getPinFromFileName(file.name);
      if (!seenImagePins.has(pin)) {
        seenImagePins.add(pin);
        uniqueImagesByPin.push(file);
      }
    });

    setSelectedImages(uniqueImagesByPin);
    setImagesLabel(uniqueImagesByPin.length ? `${uniqueImagesByPin.length} file(s) selected` : "");
    toast.success(`${uniqueImagesByPin.length} unique JPG/PNG images selected`);
  };

  const uploadAll = async () => {
    if (!excelData.length) {
      toast.error("Please upload sheet data first");
      return;
    }

    setSubmitting(true);
    try {
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

      const requestErrorMessage = (error) => {
        const status = error?.response?.status;
        const data = error?.response?.data;
        const serverMsg =
          typeof data === "string"
            ? data
            : data?.error || data?.message || data?.details || "";
        const suffix = serverMsg ? `: ${serverMsg}` : "";
        return status ? `Request failed (${status})${suffix}` : "Network error";
      };

      const postWithRetry = async (url, body, config) => {
        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
          try {
            return await axios.post(url, body, config);
          } catch (error) {
            const status = error?.response?.status;
            const retryable = status === 429 || (status >= 500 && status <= 599) || !status;
            if (!retryable || attempt === maxAttempts) throw error;
            await sleep(500 * attempt);
          }
        }
      };

      const chunkArray = (arr, size) => {
        const result = [];
        for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
        return result;
      };

      // First upload the batch to get the batchId
      const firstBatch = excelData.slice(0, 50);
      let response = await postWithRetry(
        "/properties/bulk",
        { properties: firstBatch, userId: user.id },
        { timeout: 180000 }
      );
      const batchId = response.data.batchId;

      // Upload remaining batches with the same batchId
      const remainingBatches = chunkArray(excelData.slice(50), 50);
      for (const batch of remainingBatches) {
        await postWithRetry(
          "/properties/bulk",
          { properties: batch, userId: user.id, batchId },
          { timeout: 180000 }
        );
      }

      const pins = excelData.map(row => row.pin);
      let imageSuccess = 0;
      let imageFailed = 0;

      for (const pin of pins) {
        try {
          const imageFile = matchedImages[pin];

          if (!imageFile) {
            console.warn(`No image found for PIN: ${pin}`);
            continue;
          }

          console.log(`Matching image found for PIN: ${pin}. Uploading...`);
          const dims = await readImageDimensions(imageFile);

          const formData = new FormData();
          formData.append("image", imageFile, `${pin}.jpg`);
          formData.append("userId", user.id);

          // Keep a marker for the backend in case it supports orientation metadata.
          formData.append("isPortrait", String(dims.height >= dims.width));

          const row = excelRowByPin[pin] || {};
          formData.append("circle", row.circle || "");
          formData.append("location", row.location || "");
          formData.append("occupationstatus", row.occupationstatus || "");
          formData.append("floor_admin", row.floor_admin || "");
          formData.append("landuse_admin", row.landuse_admin || "");
          formData.append("landarea_admin", row.landarea_admin || "");
          formData.append("coveredarea_admin", row.coveredarea_admin || "");
          formData.append("floors_pred", row.floors_pred || "");
          formData.append("land_use_pred", row.land_use_pred || "");
          formData.append("coveredarea_combined_pred", row.coveredarea_combined_pred || "");
          formData.append("landarea_combined_pred", row.landarea_combined_pred || "");
          formData.append("exp_garv", row.exp_garv || "");
          formData.append("admin_garv", row.admin_garv || "");
          formData.append("reassess", row.reassess || "");
          formData.append("floors_ml", row.floors_ml || "");
          formData.append("landuse_ml", row.landuse_ml || "");
          formData.append("landarea_ml", row.landarea_ml || "");
          formData.append("coveredarea_ml", row.coveredarea_ml || "");
          formData.append("refno", row.refno || "");

          await axios.put(`/properties/data-notfiltered/${encodeURIComponent(pin)}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 180000,
          });
          imageSuccess += 1;
        } catch (error) {
          console.error(`Image upload failed for pin ${pin}`, error);
          imageFailed += 1;
        }
      }

      toast.success(
        `Bulk data uploaded (${excelData.length} rows) as Batch #${batchId}. Images attached: ${imageSuccess}. Failed: ${imageFailed}.`
      );

      // Clear state after success
      setExcelData([]);
      setSelectedImages([]);
      setCsvFileName("");
      setImagesLabel("");
    } catch (error) {
      console.error(error);
      toast.error(requestErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen flex bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-y-auto p-8">
        <h1 className="text-3xl font-bold text-gray-900">Bulk Property + Image Upload</h1>
        <p className="mt-2 text-gray-600">
          Upload an Excel (.xlsx/.xls) or CSV file and matching JPG/PNG images in one place. Image names must be exact PIN values.
        </p>

        <div className="mt-8 grid grid-cols-1 xl:grid-cols-2 gap-6">
          <section className="bg-white rounded-xl shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">1) Upload Excel (.xlsx/.xls) or CSV (.csv)</h2>
            <div className="flex items-center gap-3">
              <input
                id="bulk-sheet-upload"
                type="file"
                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                onChange={handleExcelUpload}
                className="sr-only"
              />
              <label
                htmlFor="bulk-sheet-upload"
                className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
              >
                Choose file
              </label>
              <span className="text-sm text-gray-700 truncate">
                {csvFileName || "No file chosen"}
              </span>
            </div>
            <p className="mt-3 text-sm text-gray-600">
              Rows loaded: <span className="font-semibold">{excelData.length}</span>
            </p>
          </section>

          <section className="bg-white rounded-xl shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">2) Upload JPG/PNG images (bulk)</h2>
            <div className="flex items-center gap-3">
              <input
                id="bulk-images-upload"
                type="file"
                accept=".jpg,.jpeg,image/jpeg,image/png"
                multiple
                onChange={handleImagesUpload}
                className="sr-only"
              />
              <label
                htmlFor="bulk-images-upload"
                className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
              >
                Choose files
              </label>
              <span className="text-sm text-gray-700 truncate">
                {imagesLabel || "No files chosen"}
              </span>
            </div>
            <p className="mt-3 text-sm text-gray-600">
              Images selected: <span className="font-semibold">{selectedImages.length}</span>
            </p>
            <p className="text-sm text-gray-600">
              Matched by PIN filename: <span className="font-semibold">{Object.keys(matchedImages).length}</span>
            </p>
            <p className="text-sm text-gray-600">
              Unmatched images: <span className="font-semibold">{unmatchedImages.length}</span>
            </p>
          </section>
        </div>

        {!!excelData.length && (
          <section className="mt-6 bg-white rounded-xl shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Preview</h3>
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 text-left">
                    <th className="px-3 py-2">PIN</th>
                    <th className="px-3 py-2">Circle</th>
                    <th className="px-3 py-2">Location</th>
                    <th className="px-3 py-2">Group</th>
                  </tr>
                </thead>
                <tbody>
                  {excelData.map((row, idx) => (
                    <tr key={`${row.pin}-${idx}`} className="border-b">
                      <td className="px-3 py-2">{row.pin}</td>
                      <td className="px-3 py-2">{row.circle || "-"}</td>
                      <td className="px-3 py-2">{row.location || "-"}</td>
                      <td className="px-3 py-2">{row.group || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="mt-6 bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Matched Image Preview</h3>
          {!Object.keys(matchedImages).length ? (
            <p className="text-sm text-gray-500">No matched images yet.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {Object.entries(matchedImages)
                .map(([pin, file]) => (
                  <div key={pin} className="border rounded-lg p-2">
                    <div className="aspect-[3/4] bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={pin}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <p className="mt-2 text-xs font-medium text-gray-700 truncate">PIN: {pin}</p>
                  </div>
                ))}
            </div>
          )}
        </section>

        <div className="mt-8">
          <button
            onClick={uploadAll}
            disabled={submitting || !excelData.length}
            className="px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60"
          >
            {submitting ? "Uploading..." : "Upload Bulk Data + Matched Images"}
          </button>
        </div>
      </main>
    </div>
  );
};

export default BulkUpload;
